import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { toaster } from 'components/toaster/toaster';
import { useEnableCartV2Experiment } from 'experiments/use-enable-cart-v2-experiment';
import { useCart } from 'hooks/use-cart';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { getCmsOffersMapByCmsId, isDiscountOffer } from 'state/loyalty/utils';

const hasBlockingErrors = (errorCode: IncentiveEvaluationErrorCodes): boolean =>
  !FixableEvaluationErrors.has(errorCode);

const getOfferId = (offer: LoyaltyAppliedOffer | LoyaltyOffer): string | undefined | null => {
  if ('loyaltyEngineId' in offer) {
    return offer.loyaltyEngineId;
  } else if ('id' in offer) {
    return offer.id;
  }
  return null;
};

/**
 * This hook is used to remove an expired offer from the cart and displays a toast notification to the user.
 * Once the offersFeedbackMap is available, it will check if there is an offer with an error without threshold.
 * If there is, it will remove the offer from the cart and display a toast notification to the user.
 */
export const useRemoveExpiredOfferFromCart = () => {
  const enableCartV2 = useEnableCartV2Experiment();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { cartEntries, removeFromCart } = useCart();

  const toastWasDisplayed = useRef(false);

  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const appliedLoyaltyDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);

  const shouldRemoveOffer = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer) => {
      const offerId = getOfferId(offer);
      if (!offerId) {
        return true;
      }

      const cmsOffersMap = getCmsOffersMapByCmsId(cmsOffers);
      const selectedCMSOffer = 'cmsId' in offer && offer.cmsId ? cmsOffersMap[offer.cmsId] : null;

      const isSelectedLoyaltyOfferAvailable = selectedCMSOffer
        ? checkLoyaltyOfferAvailability(selectedCMSOffer)
        : false;

      const errorCodes = offersFeedbackMap[offerId];
      const someErrorIsNotFixable = errorCodes?.some(error =>
        hasBlockingErrors(error?.code as IncentiveEvaluationErrorCodes)
      );

      return (
        (selectedCMSOffer && !isSelectedLoyaltyOfferAvailable) ||
        (errorCodes && someErrorIsNotFixable)
      );
    },
    [checkLoyaltyOfferAvailability, cmsOffers, offersFeedbackMap]
  );

  const removeOffer = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer) => {
      dispatch(actions.loyalty.removeAppliedOffer(offer));
      if ('loyaltyEngineId' in offer && isDiscountOffer(offer)) {
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
      } else if ('cartId' in offer && offer.cartId) {
        removeFromCart({ cartId: offer.cartId });
      }
      toaster.informative(formatMessage({ id: 'removeExpiredOfferItem' }));
      toastWasDisplayed.current = true;
    },
    [dispatch, formatMessage, removeFromCart]
  );

  const handleExpiredOfferRemoval = useCallback(() => {
    if (toastWasDisplayed.current || !enableCartV2) {
      return;
    }

    const appliedOffer = appliedOffers.find(offer =>
      cartEntries.some(entry => entry.cartId === offer.cartId)
    );

    // Only one selected offer can exist in the cart.
    const offer = appliedOffer || appliedLoyaltyDiscountOffer;

    if (offer && shouldRemoveOffer(offer)) {
      removeOffer(offer);
    }
  }, [
    enableCartV2,
    appliedOffers,
    appliedLoyaltyDiscountOffer,
    shouldRemoveOffer,
    cartEntries,
    removeOffer,
  ]);

  useEffect(() => {
    if (offersFeedbackMap) {
      handleExpiredOfferRemoval();
    }
  }, [offersFeedbackMap, handleExpiredOfferRemoval]);
};
